'use client';

import { useState, useCallback } from 'react';
import { tmaActions } from '../bootstrap/tmaActions';
import { rawField, stringField } from '../lib/jsonBody';
import { useApiClient } from '../contexts/ApiContext';
import { useProfileData } from '../queries/useProfileQueries';
import { useRuntime } from '../runtime/RuntimeContext';
import { getBotUsername, getMiniAppName } from '../utils/config';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { isBigWin as computeBigWin } from '../utils/bigWin';
import { shareOrCopyLink } from '../lib/browserSurface';

interface UseShareWinOptions {
  inventoryItemId: string;
  itemName: string;
  itemPrice: number;
  itemHashName: string;
  sourcePrice?: number;
  caption: string;
  buttonText: string;
  fallbackText: string;
}

export function useShareWin({ inventoryItemId, itemName, itemPrice, itemHashName, sourcePrice, caption, buttonText, fallbackText }: UseShareWinOptions) {
  const [isSharing, setIsSharing] = useState(false);
  const apiClient = useApiClient();
  const profile = useProfileData();
  const runtime = useRuntime();

  const isBigWin = computeBigWin(itemPrice, sourcePrice);

  const share = useCallback(async () => {
    if (!profile?.id || isSharing) return;

    const botUsername = getBotUsername();
    const appName = getMiniAppName();
    const startapp = `item_${inventoryItemId}--ref_${profile.id}`;
    const deepLink =
      runtime === 'web'
        ? `${window.location.origin}/inventory/${inventoryItemId}`
        : `https://t.me/${botUsername}/${appName}?startapp=${startapp}`;

    const apiBaseUrl = getApiBaseUrl();
    const itemImageUrl = `${apiBaseUrl}/api/public/item-image?hashName=${encodeURIComponent(itemHashName)}`;

    if (await tmaActions.isShareMessageAvailable()) {
      setIsSharing(true);
      try {
        const { preparedMessageId } = await apiClient.api.userControllerSavePreparedInlineMessage({
          imageUrl: itemImageUrl,
          animatedItem: { inventoryItemId },
          caption,
          title: itemName,
          button: { text: buttonText, url: deepLink },
        });

        await tmaActions.shareMessage(preparedMessageId);
      } catch (error) {
        if (stringField(error, 'message')?.includes('ERR_CANCELLED') || rawField(error, 'type') === 'ERR_CANCELLED') {
          return;
        }
        throw error;
      } finally {
        setIsSharing(false);
      }
    } else if (window.location.hash.includes('tgWebAppData')) {
      const message = encodeURIComponent(fallbackText);
      await tmaActions.openTelegramLink(`https://t.me/share/url?url=${deepLink}&text=${message}`);
    } else {
      await shareOrCopyLink(fallbackText, deepLink);
    }
  }, [profile?.id, isSharing, apiClient, inventoryItemId, itemName, itemHashName, caption, buttonText, fallbackText, runtime]);

  return { share, isSharing, isBigWin };
}
